/*
 * Reusuable d3 for heatmaps
 * Stylesheet dependent on head file
 * Currently built for y Axis Locked and x Axis Unlocked
 */
import ReactDOMServer from 'react-dom/server';
import * as d3 from 'd3';
import styles from './Heatmap.css';
import React from "react"; // eslint-disable-line

export default function d3Heatmap() {
  // D3 selections
  let selection = null;
  let xAxis = null;
  let yAxis = null;
  let svg = null;

  let xScale = null;
  let yScale = null;
  let xCountScale = null;

  // Options defaults declaration
  let addColumnCountAxis = false;
  let cellBorderSize = null;
  let cellHeight = 15;
  let cellWidth = 15;
  let color = () => {};
  let column = [];
  let columnHeaderRotation = -65;
  let data = null;
  let drag = null;
  let height = 0;
  let hideXAxis = false;
  let info = {};
  let lockXAxis = false;
  const lockYAxis = true;
  let margins = null;
  let maxBodyWidth = 800;
  let row = [];
  let rowBuffer = 0; // ie. the distance between row headers and heatmap itself
  let tooltips = {};
  let width = 0;
  let tooltipOffset = null;
  let onCellHoverCallback = null;
  let onCellLeaveCallback = null;
  let xAxisTextOffset = null;
  const yAxisFontSize = 11;
  let yAxisWidth = 0;
  let id = 'd3-heatmap-container';

  const colorScale = d => color(d.value);

  function createFixedXAxis() {
    xAxis = selection
      .append('svg')
      .attr('class', `${styles.xAxis}`)
      .attr('height', 100)
      .attr('width', width)
      .style('display', 'inline-block')
      .append('g');

    xAxis
      .selectAll('g')
      .data(column)
      .enter()
      .append('g')
      .attr('class', `${styles.xAxis}`)
      .attr('transform', `translate(0, -${cellWidth / 2})`)
      .append('text')
      .attr('x', (d, i) => i * cellWidth)
      .attr('y', 90)
      .attr('height', 100)
      .attr('width', cellWidth)
      .style('font-size', 'smaller')
      .style('text-anchor', 'start')
      .attr(
        'transform',
        (d, i) => `rotate(${columnHeaderRotation} ${i * cellWidth} 90)`,
      )
      .text(d => d.name);
  }

  // Create a fixed Y Axis next to the heatmap
  function createFixedYAxis() {
    // Styling based on if the row header has a link
    const hmStyle = {
      fill: '',
      pointer: '',
    };
    if (row[0] && (row[0].link || typeof row[0].onClick === 'function')) {
      hmStyle.fill = '#337ab7';
      hmStyle.pointer = 'pointer';
    }

    // Render the fixed Y Axis
    yAxis = selection
      .append('svg')
      .attr('class', `${styles.yAxis}`)
      .attr('height', height + margins.top + margins.bottom)
      .style('float', 'left')
      .style('display', 'inline-block')
      .append('g');

    const length = yAxis
      .selectAll('g')
      .data(row)
      .enter()
      .append('text')
      .text(d => d.name)
      .attr('font-size', yAxisFontSize);

    yAxisWidth = Math.max(
      d3.max(length.nodes(), n => n.getComputedTextLength() + rowBuffer),
      yAxisWidth,
    );
    length.remove();
    if (Number.isNaN(yAxisWidth)) yAxisWidth = 0;

    selection.selectAll(`.${styles.yAxis}`).attr('width', yAxisWidth);
    yAxis
      .selectAll('g')
      .data(row)
      .enter()
      .append('g')
      .attr('id', (d, i) => `${row[i].name}`)
      .append('text')
      .attr('class', d =>
        typeof d.onClick === 'function'
          ? `${styles.yAxis} ${styles.yAxisLink}`
          : `${styles.yAxis} ${styles.yAxisText}`,
      )
      .style('fill', hmStyle.fill)
      .style('font-size', `${yAxisFontSize}px`)
      .style('cursor', hmStyle.pointer)
      .attr('x', yAxisWidth - rowBuffer)
      .attr(
        'y',
        (d, i) =>
          i * cellHeight + margins.top + cellHeight / 2 + yAxisFontSize / 2 - 1,
      )
      .attr('width', yAxisWidth)
      .attr('height', cellHeight)
      .attr('text-anchor', 'end')
      .on('click', d => typeof d.onClick === 'function' && d.onClick())
      .text(d => d.name);
  }

  // Render Heatmap div and svg
  function chart(_selection) {
    selection = _selection;

    width = cellWidth * column.length;
    height = cellHeight * row.length;

    // Render the fixed x and y axis when necessary
    if (lockXAxis) {
      margins.top = 0;
      createFixedXAxis();
    }
    if (lockYAxis) {
      margins.left = 0;
      createFixedYAxis();
    }

    // Create array of strings for the built in domain rendering of row and header text
    const rowValues = row.map(rowHeader => rowHeader.name);
    const columnValues = column.map(columnHeader => columnHeader.name);

    const paddedWidth = width + margins.right + margins.left;
    const paddedHeight = height + margins.top + margins.bottom;

    const bodyWidth = Math.max(Math.min(paddedWidth, maxBodyWidth), 0);
    const containerWidth = yAxisWidth + bodyWidth;
    yScale = d3
      .scaleBand()
      .range([0, rowValues.length * cellHeight])
      .domain(rowValues);

    xScale = d3
      .scaleBand()
      .domain([...Array(columnValues.length).keys()])
      .range([0, columnValues.length * cellWidth]);

    xCountScale = d3
      .scaleBand()
      .domain([...Array(columnValues.length).keys()])
      .range([0, columnValues.length * cellWidth]);

    // Add axis headers if necessary
    if (info.yHeaderName) {
      yAxis
        .append('text')
        .attr('x', yAxisWidth - rowBuffer)
        .attr(
          'y',
          -cellHeight + margins.top + cellHeight / 2 + yAxisFontSize / 2,
        )
        .attr('width', yAxisWidth)
        .attr('height', cellHeight)
        .attr('text-anchor', 'end')
        .attr('class', `${styles.axisHeaderName}`)
        .text(`${info.yHeaderName}`);
    }

    if (info.xHeaderName) {
      selection
        .append('div')
        .style('display', 'inline-block')
        .style('position', 'absolute')
        .style('width', `${bodyWidth}px`)
        .style('text-align', 'center')
        .attr('class', `${styles.axisHeaderName}`)
        .html(`${info.xHeaderName}`);
    }

    // Begin default drag code
    let x0 = 0;
    let x1 = 0;
    let deltaX = 0;
    let scroll0 = 0;

    // Drag fn's from https://bl.ocks.org/mforando/90a60be1885c4882928776b46aa0c40a
    function dragStartedDefault(e) {
      // Clear tooltip immediately when drag starts - user is navigating, not inspecting
      if (onCellLeaveCallback) {
        onCellLeaveCallback();
      }
      // Get initial x position
      x0 = e.x;
      scroll0 = selection.select(`#${id}`).node().scrollLeft;
    }

    function draggedDefault(e) {
      // Calculate change in x, and the associated change in scrolling
      x1 = e.x;
      deltaX = x1 - x0;

      // Move scroller to starting scroll value + change in x
      selection.select(`#${id}`).property('scrollLeft', scroll0 - deltaX);
    }

    const dragged = () => {
      if (typeof drag === 'function') drag(d3.event);
      else if (d3.event.type === 'start') dragStartedDefault(d3.event);
      else if (d3.event.type === 'drag') draggedDefault(d3.event);
    };

    // Begin drawing main heatmap svg
    selection
      .selectAll(`.${styles.container}`)
      .style('width', `${containerWidth}px`);
    svg = selection
      .append('div')
      .attr('class', `${styles.heatmap}`)
      .attr('id', id)
      .style('width', `${bodyWidth}px`)
      .style('display', 'inline-block')
      .style('overflow-x', paddedWidth > bodyWidth ? 'scroll' : 'visible')
      .style('line-height', 0)
      .append('svg')
      .attr('width', paddedWidth)
      .attr('height', paddedHeight)
      .attr('id', info.heatmapId)
      .call(
        d3
          .drag()
          .on('start', dragged)
          .on('drag', dragged)
          .on('end', dragged),
      )
      .append('g')
      .attr('width', paddedWidth)
      .attr('height', paddedHeight)
      .attr('transform', `translate(${margins.left},${margins.top})`);

    svg
      .selectAll('g')
      .data(data)
      .enter()
      .append('g')
      .append('rect')
      .attr('x', d => d.column * cellWidth)
      .attr('y', d => d.row * cellHeight)
      .attr('id', d => `${d.row}-${d.column}`)
      .style('fill', colorScale)
      .style('outline-color', 'white')
      .style('outline-width', `${cellBorderSize}px`)
      .style('outline-style', 'solid')
      .style('outline-offset', '-1px')
      .attr('width', cellWidth)
      .attr('height', cellHeight)
      .on('mouseover', function(d) {
        d3.select(this).style('outline-color', 'gray');

        // Use React callback for tooltip if available, otherwise fall back to D3 tooltip
        if (onCellHoverCallback) {
          // Call immediately - React handles timing/debouncing
          onCellHoverCallback(d, this);
        } else {
          // Legacy D3 tooltip positioning (fallback)
          const e = d3.event;
          row[d.row].timeout = setTimeout(() => {
            const rowValue = row[d.row].name;
            let tHtml = null;
            if (typeof tooltips === 'function') {
              tHtml = ReactDOMServer.renderToStaticMarkup(tooltips(d));
            } else if (rowValue in tooltips && tooltips[rowValue][d.column]) {
              tHtml = ReactDOMServer.renderToStaticMarkup(
                tooltips[rowValue][d.column],
              );
            }
            if (tHtml) {
              selection
                .select(`.${styles.tooltip}`)
                .html(tHtml)
                .style('display', 'inline-block')
                .style('left', `${e.clientX + tooltipOffset.x}px`)
                .style('top', `${e.clientY - tooltipOffset.y}px`);
            }
          }, 5);
        }
      })
      .on('mouseout', function(d) {
        // eslint-disable-line
        // Hide tooltip
        clearTimeout(row[d.row].timeout);
        d3.select(this).style('outline-color', 'white');
        if (onCellLeaveCallback) {
          onCellLeaveCallback();
        } else {
          selection.select(`.${styles.tooltip}`).style('display', 'none');
        }
      });

    if (
      info.targetXIndex !== null &&
      info.targetXIndex > -1 &&
      typeof info.targetXIndex !== 'undefined'
    ) {
      svg
        .append('rect')
        .attr('x', info.targetXIndex * cellWidth)
        .attr('y', 0)
        .attr('fill', 'none')
        .attr('stroke', '#b3b3b3')
        .attr('stroke-width', '1.5px')
        .attr('width', cellWidth)
        .attr('height', height);
    }
    // Render built in x and y axis if the locked versions aren't used
    if (!lockXAxis) {
      svg
        .append('g')
        .attr('class', `${styles.xAxis}`)
        .call(
          d3
            .axisTop(xScale)
            .tickFormat((d, i) => columnValues[i])
            .tickSizeOuter(0),
        )
        .selectAll('text')
        .attr('class', (d, i) =>
          typeof column[i].onClick === 'function'
            ? `${styles.xAxis} ${styles.xAxisLink}`
            : `${styles.xAxis} ${styles.xAxisText}`,
        )
        .style('font-weight', (d, i) => {
          if (info.targetXIndex === i) return 'bold';
          return '';
        })
        .attr('dx', `${xAxisTextOffset.x}em`)
        .attr('dy', `${xAxisTextOffset.y}em`)
        .attr('transform', `rotate(${columnHeaderRotation})`)
        .attr('id', (d, i) => column[i].name || i)
        .on('click', (d, i) =>
          column[i].onClick ? column[i].onClick(d) : null,
        );

      // Hide XAxis if so desired
      svg
        .selectAll(`.${styles.xAxis}`)
        .style('display', hideXAxis ? 'none' : 'inline');

      // Give every axis an ID
      svg
        .selectAll(`.${styles.xAxis}`)
        .selectAll('g')
        .style('display', (d, i) => (column[i].hidden ? 'none' : ''))
        .attr('id', (d, i) => column[i].name);

      if (addColumnCountAxis) {
        svg
          .append('g')
          .attr('class', `bottomAxis`)
          .attr('transform', `translate (0 ${height})`)
          .call(
            d3
              .axisBottom(xCountScale)
              .tickValues(
                [...Array(columnValues.length).keys()].filter(
                  (v, i) => i % 20 === 0,
                ),
              )
              .tickSizeOuter(0),
          )
          .selectAll('text')
          .style('font-weight', 400)
          .attr('y', 2);

        svg.call(g =>
          g
            .selectAll('.tick line')
            .attr('stroke', 'black')
            .attr('stroke-width', 0.4)
            .attr('y2', 5),
        );
        svg.call(g =>
          g
            .selectAll('.bottomAxis')
            .selectAll('.tick line')
            .attr('y2', -5),
        );
      }
    }

    if (!lockYAxis) {
      svg
        .append('g')
        .attr('transform', `translate(0, -${cellHeight / 2})`)
        .call(d3.axisLeft(yScale))
        .selectAll('text')
        .attr('dy', '1em');
    }

    selection.append('div').attr('class', styles.tooltip);
  }

  // Used to scroll selected column as close to the center as possible
  chart.scrollIntoView = () => {
    const ele = svg.select(`[id="${info.targetXPos}"]`).node();
    if (ele)
      ele.scrollIntoView({
        behavior: 'auto',
        block: 'center',
        inline: 'center',
      });
  };

  // Setter functions for the d3 inputs
  chart.data = (...args) => {
    if (!args.length) return data;
    const [dataIn] = args;
    data = dataIn;
    return chart;
  };

  chart.rows = (...args) => {
    if (!args.length) return row;
    const [rowIn] = args;
    row = rowIn;
    return chart;
  };

  chart.columns = (...args) => {
    if (!args.length) return column;
    const [columnIn] = args;
    column = columnIn;
    return chart;
  };

  chart.colors = (...args) => {
    if (!args.length) return color;
    const [colorIn] = args;
    color = colorIn;
    return chart;
  };

  chart.tooltips = (...args) => {
    if (!args.length) return tooltips;
    const [tooltipsIn] = args;
    tooltips = tooltipsIn;
    return chart;
  };

  chart.info = (...args) => {
    if (!args.length) return info;
    const [infoIn] = args;
    info = infoIn;
    return chart;
  };

  chart.maxBodyWidth = (...args) => {
    if (!args.length) return maxBodyWidth;
    const [bodyWidthIn] = args;
    maxBodyWidth = bodyWidthIn;
    return chart;
  };

  chart.drag = (...args) => {
    if (!args.length) return drag;
    const [dragIn] = args;
    drag = dragIn;
    return chart;
  };

  chart.onCellHover = (...args) => {
    if (!args.length) return onCellHoverCallback;
    const [callbackIn] = args;
    onCellHoverCallback = callbackIn;
    return chart;
  };

  chart.onCellLeave = (...args) => {
    if (!args.length) return onCellLeaveCallback;
    const [callbackIn] = args;
    onCellLeaveCallback = callbackIn;
    return chart;
  };

  chart.setLockXAxis = lockX => {
    lockXAxis = lockX;
    return chart;
  };

  chart.setColumnHeaderRotation = rot => {
    columnHeaderRotation = rot;
    return chart;
  };

  chart.setCellWidth = cWidth => {
    cellWidth = cWidth;
    return chart;
  };

  chart.setCellHeight = cHeight => {
    cellHeight = cHeight;
    return chart;
  };

  chart.id = (...args) => {
    if (!args.length) return id;
    const [idIn] = args;
    id = idIn;
    return chart;
  };

  chart.setLayout = layoutIn => {
    margins = layoutIn.margins; // eslint-disable-line prefer-destructuring
    yAxisWidth = layoutIn.yAxisWidth; // eslint-disable-line prefer-destructuring
    rowBuffer = layoutIn.rowBuffer; // eslint-disable-line prefer-destructuring
    cellBorderSize = layoutIn.cellBorderSize; // eslint-disable-line prefer-destructuring
    addColumnCountAxis = layoutIn.addColumnCountAxis; // eslint-disable-line prefer-destructuring
    xAxisTextOffset = layoutIn.xAxisTextOffset; // eslint-disable-line prefer-destructuring
    tooltipOffset = layoutIn.tooltipOffset; // eslint-disable-line prefer-destructuring
    hideXAxis = layoutIn.hideXAxis; // eslint-disable-line prefer-destructuring
    return chart;
  };

  chart.getCellWidth = () => cellWidth;
  chart.getCellHeight = () => cellHeight;
  chart.getRowHeaderWidth = () => yAxisWidth;

  return chart;
}
